import {
  CaretCircleDoubleRight,
  CurrencyCircleDollar,
  HandCoins,
  Info,
  Money,
} from '@phosphor-icons/react';
import { Tooltip } from 'antd';
import { useScreenView } from '../../../../../../utils/ScreenViewContext';
import { BalancesResponse } from '../../../../../../types/apiTypes';
import { formatNumberCommasWithDollar } from '../../../../../../utils/CurrencyUtils';
import { ClipLoader } from 'react-spinners';
import { DemoDataWrapper } from '../components/DemoDataBadge';
import { CASH_ADVANCE_LOCKED_COPY } from '../../../../../../constants/bankingCopy';

function BalancesWidget({
  balances,
  loadingBalances,
  isPreActive,
  prev60DayAvgRevenue = 0,
}: {
  balances: BalancesResponse;
  loadingBalances: boolean;
  isPreActive: boolean;
  /** Average monthly revenue over the previous 60 days (two completed months). */
  prev60DayAvgRevenue?: number;
}) {
  const { screenView } = useScreenView();

  // Strict false check: older API responses without the flag keep today's rendering
  const isCashAdvanceLocked = balances?.isCashAdvanceActive === false;

  // MTD revenue compared against the previous 60-day average monthly revenue.
  // Null when we lack enough history to compute a 60-day average.
  const prev60Pct =
    prev60DayAvgRevenue > 0
      ? Math.round(
          (((balances?.mtdRevenue || 0) - prev60DayAvgRevenue) /
            prev60DayAvgRevenue) *
            100
        )
      : null;

  const renderPacePhrase = (pace: number) => {
    if (pace >= 90 && pace <= 101) {
      return <h2 className="text-[14px] text-[#0A112F]">{`On schedule`}</h2>;
    } else if (pace > 101) {
      return (
        <h2 className="text-[14px] text-secondary">
          {`Ahead of schedule (+${Math.round(pace - 100)}%)`}
        </h2>
      );
    } else if (pace < 90) {
      return (
        <h2 className="text-[14px] text-danger">
          {`Behind schedule (-${Math.round(100 - pace)}%)`}
        </h2>
      );
    }
  };

  const renderScreenView = (screenView: string) => {
    switch (screenView) {
      case 'desktop':
      case 'tablet':
      case 'mobile':
        return (
          <div className="grid w-full grid-cols-1 items-stretch gap-[12px] lg:grid-cols-2 lg:gap-[16px]">
            {!loadingBalances ? (
              <div className="flex w-full flex-row items-center gap-x-[16px] border-[1px] border-bordergray bg-white p-3 align-middle lg:min-h-[136px] lg:p-4">
                <div className="max-w-[20%] rounded-[4px] border-[1px] border-[#EAECF0] bg-[#F5F6F7] p-[10px] lg:p-[12px]">
                  <Money size={30} />
                </div>
                <div className="flex w-[80%] flex-col items-start align-middle">
                  <h2 className="text-[16px] text-[#0A112F]">
                    Revenue for{' '}
                    {
                      new Date()
                        .toLocaleString('en-US', {
                          month: 'long',
                          year: 'numeric',
                        })
                        .split(' ')[0]
                    }
                  </h2>
                  <h1 className="text-[24px] font-[700] leading-[32px] text-[#1D213B] lg:text-[30px] lg:leading-[40px]">
                    {formatNumberCommasWithDollar(balances?.mtdRevenue || 0)}
                    <span className="text-[16px] leading-[24px] text-graysecondary lg:text-[20px] lg:leading-[28px]">
                      .
                      {(balances?.mtdRevenue % 1 || 0)
                        .toFixed(2)
                        .split('.')[1] || '00'}
                    </span>
                  </h1>
                  {prev60Pct !== null && (
                    <h2
                      className={`text-[14px] ${prev60Pct > 0 ? 'text-secondary' : prev60Pct === 0 ? 'text-[#0A112F] opacity-50' : 'text-danger'}`}
                    >
                      {prev60Pct > 0
                        ? `Up ${prev60Pct}%`
                        : prev60Pct === 0
                          ? 'Even'
                          : `Down ${Math.abs(prev60Pct)}%`}{' '}
                      vs prev 60-day avg{' '}
                      <span className="opacity-60">
                        ({formatNumberCommasWithDollar(prev60DayAvgRevenue)})
                      </span>
                    </h2>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex h-[96px] max-h-[96px] w-full items-center justify-center border-[1px] border-bordergray bg-white lg:h-[116px] lg:max-h-[116px]">
                {' '}
                <ClipLoader
                  color="#1D213B"
                  loading={loadingBalances}
                  size={40}
                  aria-label="Loading Spinner"
                  data-testid="loader"
                  className="flex h-full w-full justify-center"
                />{' '}
              </div>
            )}

            {!loadingBalances ? (
              <div className="flex w-full flex-row items-center gap-x-[16px] border-[1px] border-bordergray bg-white p-3 align-middle lg:min-h-[136px] lg:p-4">
                <div className="max-w-[20%] rounded-[4px] border-[1px] border-[#EAECF0] bg-[#F5F6F7] p-[10px] lg:p-[12px]">
                  <CurrencyCircleDollar size={30} />
                </div>
                <div className="flex w-[80%] flex-col items-start align-middle">
                  <div className="flex w-full flex-row items-center justify-between align-middle">
                    <h2 className="flex h-[24px] items-end text-[16px] text-[#0A112F]">
                      <p className="leading-[16px]">Available </p>
                      <span>
                        <img
                          alt="tach-logo"
                          src="/tach.svg"
                          className="mb-[2.6px] ml-1 mr-1 h-[11px]"
                        />
                      </span>
                      <p className="leading-[16px]">Balance</p>
                    </h2>
                  </div>
                  <h1 className="text-[24px] font-[700] leading-[32px] text-primary lg:text-[30px] lg:leading-[40px]">
                    {formatNumberCommasWithDollar(
                      Math.floor(balances?.budgetedBalance || 0)
                    )}
                    <span className="text-[16px] leading-[24px] text-graysecondary lg:text-[20px] lg:leading-[28px]">
                      .
                      {(balances?.budgetedBalance % 1 || 0)
                        .toFixed(2)
                        .split('.')[1] || '00'}
                    </span>
                  </h1>
                  {renderPacePhrase(
                    balances?.savingPacePercentageDifference || 0
                  )}
                </div>
              </div>
            ) : (
              <div className="flex h-[96px] max-h-[96px] w-full items-center justify-center border-[1px] border-bordergray bg-white lg:h-[116px] lg:max-h-[116px]">
                {' '}
                <ClipLoader
                  color="#1D213B"
                  loading={loadingBalances}
                  size={40}
                  aria-label="Loading Spinner"
                  data-testid="loader"
                  className="flex h-full w-full justify-center"
                />{' '}
              </div>
            )}

            {!loadingBalances ? (
              <div className="flex w-full flex-row items-center gap-x-[16px] border-[1px] border-bordergray bg-white p-3 align-middle lg:min-h-[136px] lg:p-4">
                <div className="max-w-[20%] rounded-[4px] border-[1px] border-[#EAECF0] bg-[#F5F6F7] p-[10px] lg:p-[12px]">
                  <CaretCircleDoubleRight size={30} />
                </div>
                <div className="flex w-[80%] flex-col items-start align-middle">
                  <h2 className="text-[16px] text-[#0A112F]">
                    Net Take Home for{' '}
                    {
                      new Date()
                        .toLocaleString('en-US', {
                          month: 'long',
                          year: 'numeric',
                        })
                        .split(' ')[0]
                    }
                  </h2>
                  <h1 className="text-[24px] font-[700] leading-[32px] text-[#1D213B] lg:text-[30px] lg:leading-[40px]">
                    {formatNumberCommasWithDollar(balances?.grossProfit || 0)}
                    <span className="text-[16px] leading-[24px] text-graysecondary lg:text-[20px] lg:leading-[28px]">
                      .
                      {(balances?.grossProfit % 1 || 0)
                        .toFixed(2)
                        .split('.')[1] || '00'}
                    </span>
                  </h1>
                  {((balances?.pendingTransfer ?? 0) >= 1 ||
                    (balances?.availableTakeHomePay ?? 0) < 1) && (
                    <h2 className="text-[14px] text-[#0A112F] opacity-50">
                      Pending transfer:{' '}
                      {formatNumberCommasWithDollar(
                        balances?.pendingTransfer || 0
                      )}
                    </h2>
                  )}
                  {(balances?.availableTakeHomePay ?? 0) >= 1 && (
                    <h2 className="text-[14px] text-secondary">
                      Available take home pay:{' '}
                      {formatNumberCommasWithDollar(
                        balances?.availableTakeHomePay || 0
                      )}
                    </h2>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex h-[96px] max-h-[96px] w-full items-center justify-center border-[1px] border-bordergray bg-white lg:h-[116px] lg:max-h-[116px]">
                {' '}
                <ClipLoader
                  color="#1D213B"
                  loading={loadingBalances}
                  size={40}
                  aria-label="Loading Spinner"
                  data-testid="loader"
                  className="flex h-full w-full justify-center"
                />{' '}
              </div>
            )}

            {!loadingBalances ? (
              <div className="flex w-full flex-row items-center gap-x-[16px] border-[1px] border-bordergray bg-white p-3 align-middle lg:min-h-[136px] lg:p-4">
                <div className="max-w-[20%] rounded-[4px] border-[1px] border-[#EAECF0] bg-[#F5F6F7] p-[10px] lg:p-[12px]">
                  <HandCoins size={30} />
                </div>
                <div className="flex w-[80%] flex-col items-start align-middle">
                  <h2 className="flex items-center gap-x-1 text-[16px] text-[#0A112F]">
                    Available Cash Advance
                    <Tooltip
                      title={
                        <div className="text-[12px]">
                          <p className="mb-1 font-[600]">
                            How your cash advance works
                          </p>
                          <ul className="list-disc space-y-1 pl-4">
                            <li>
                              Your available amount is based on the business
                              revenue flowing into your TACH account — steady
                              deposits unlock it and grow it.
                            </li>
                            <li>
                              Open advances reduce your available amount —
                              payments free it back up.
                            </li>
                            <li>
                              It updates automatically as your revenue changes.
                            </li>
                          </ul>
                        </div>
                      }
                      placement="top"
                      mouseEnterDelay={0.2}
                    >
                      <Info size={16} className="cursor-help opacity-50" />
                    </Tooltip>
                  </h2>
                  <h1
                    className={`text-[24px] font-[700] leading-[32px] lg:text-[30px] lg:leading-[40px] ${isCashAdvanceLocked ? 'text-graysecondary' : 'text-primary'}`}
                  >
                    {formatNumberCommasWithDollar(
                      Math.floor(balances?.cashAdvanceAvailable || 0)
                    )}
                    <span className="text-[16px] leading-[24px] text-graysecondary lg:text-[20px] lg:leading-[28px]">
                      .
                      {(balances?.cashAdvanceAvailable % 1 || 0)
                        .toFixed(2)
                        .split('.')[1] || '00'}
                    </span>
                  </h1>
                  {isCashAdvanceLocked ? (
                    <h2 className="text-[14px] text-[#0A112F] opacity-50">
                      {CASH_ADVANCE_LOCKED_COPY}
                    </h2>
                  ) : (
                    (balances?.cashAdvanceLimit ?? 0) > 0 && (
                      <h2 className="text-[14px] text-[#0A112F] opacity-50">
                        of{' '}
                        {formatNumberCommasWithDollar(
                          balances?.cashAdvanceLimit || 0
                        )}{' '}
                        limit
                      </h2>
                    )
                  )}
                </div>
              </div>
            ) : (
              <div className="flex h-[96px] max-h-[96px] w-full items-center justify-center border-[1px] border-bordergray bg-white lg:h-[116px] lg:max-h-[116px]">
                {' '}
                <ClipLoader
                  color="#1D213B"
                  loading={loadingBalances}
                  size={40}
                  aria-label="Loading Spinner"
                  data-testid="loader"
                  className="flex h-full w-full justify-center"
                />{' '}
              </div>
            )}
          </div>
        );
    }
  };
  return (
    <DemoDataWrapper showBadge={isPreActive} layout="horizontal">
      {renderScreenView(screenView)}
    </DemoDataWrapper>
  );
}

export default BalancesWidget;
